// The nav: a glass pill with two gliding dropdowns, plain links and the one
// call to action. The dropdown viewport is shared: switching triggers GLIDES
// the box (left/size) while the old content slides out and the new one in.
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CRWIcon } from '../components/icons';
import { Logo, MetalBtn } from '../components/primitives';
import { CTA_LABEL } from '../lib/site';

type NavItem = { label: string; desc: string; hash: string };
export const NAV_GET: NavItem[] = [
  { label: 'One sentence', desc: 'Every clip sits on a sentence', hash: '#zoom' },
  { label: 'The project', desc: 'A Premiere project you can edit', hash: '#project' },
  { label: 'The deal', desc: 'A third less, every layer included', hash: '#deal' },
];
export const NAV_PROOF: NavItem[] = [
  { label: 'The case film', desc: 'The whole thing in about a minute', hash: '#film' },
  { label: 'The films', desc: 'Two finished documentaries', hash: '#films' },
  { label: 'The hours', desc: '160 by hand, 30 with us', hash: '#hours' },
];

// Smooth-scroll to a section, leaving room for the fixed nav.
export function scrollToHash(hash: string) {
  const el = hash === '#top' ? document.body : document.querySelector(hash);
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 76, behavior: 'smooth' });
}

// `routed` = rendered on a sub-page: section links navigate back to /#hash.
const NAV_KEYS = ['get', 'proof'] as const;
type NavKey = (typeof NAV_KEYS)[number];

export function SiteNav({ routed }: { routed?: boolean }) {
  const [open, setOpen] = useState<NavKey | null>(null);
  const [menu, setMenu] = useState(false);
  // at the very top of the page the pill is bare - the frost MATERIALIZES the
  // moment content scrolls beneath it (crw-nav-atop drops the glass layer)
  const [atop, setAtop] = useState(() => typeof window === 'undefined' || window.scrollY < 10);
  useEffect(() => {
    const onScroll = () => setAtop(window.scrollY < 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const navRef = useRef<HTMLElement | null>(null);
  const closeTimer = useRef<number | null>(null);
  const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---- shared gliding viewport (the NavigationMenu pattern): one panel under the
  // pill; switching triggers GLIDES the box (left/size) while the old content
  // slides out with the direction of travel and the new one slides in ----
  const trigRefs = useRef<Record<NavKey, HTMLButtonElement | null>>({ get: null, proof: null });
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [vp, setVp] = useState<{ left: number; w: number; h: number } | null>(null);
  // ghost = the outgoing content layer during a switch; dir = where IT exits to
  const [ghost, setGhost] = useState<{ k: NavKey; dir: 'left' | 'right'; n: number } | null>(null);
  const ghostTimer = useRef<number | null>(null);
  const lastShownRef = useRef<NavKey>('get');
  // glide only between two open states - a fresh open must not slide across
  // from where the panel sat when it last closed
  const glide = useRef(false);

  const openMenu = (k: NavKey) => {
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    setOpen((cur) => {
      glide.current = cur != null;
      if (cur && cur !== k && !reduced) {
        const dir: 'left' | 'right' = NAV_KEYS.indexOf(k) > NAV_KEYS.indexOf(cur) ? 'left' : 'right';
        setGhost((g) => ({ k: cur, dir, n: (g?.n ?? 0) + 1 }));
        if (ghostTimer.current != null) window.clearTimeout(ghostTimer.current);
        ghostTimer.current = window.setTimeout(() => setGhost(null), 230);
      }
      return k;
    });
  };

  // measure the active trigger + content -> position/size the viewport box
  // (layout effect: the box must be placed before paint or a fresh open flashes
  // at its previous position)
  useLayoutEffect(() => {
    if (!open) return;
    const trig = trigRefs.current[open];
    const nav = navRef.current;
    const inner = measureRef.current;
    if (!trig || !nav || !inner) return;
    const nb = nav.getBoundingClientRect();
    const tb = trig.getBoundingClientRect();
    const w = inner.offsetWidth;
    const h = inner.offsetHeight;
    // align to the trigger's left edge, but never spill past the pill's right edge
    const left = Math.max(10, Math.min(tb.left - nb.left, nb.width - w - 10));
    setVp({ left, w, h });
  }, [open]);

  // close dropdowns/menu on outside tap, scroll, or Escape
  const sheetRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menu && !open) return;
    const shut = () => { setMenu(false); setOpen(null); };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (navRef.current && !navRef.current.contains(t) && !sheetRef.current?.contains(t)) shut();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') shut(); };
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('scroll', shut, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); window.removeEventListener('scroll', shut); document.removeEventListener('keydown', onKey); };
  }, [menu, open]);

  // the mobile sheet owns the screen while open - lock the page scroll behind it
  useEffect(() => {
    if (!menu) return;
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => { document.documentElement.style.overflow = prev; };
  }, [menu]);

  const shut = () => { setMenu(false); setOpen(null); };
  const go = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    shut();
    scrollToHash(href);
  };

  // hover intent: open instantly, close after a beat so the pointer can travel
  const hoverClose = () => {
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(null), 160);
  };

  // anchor link: smooth-scroll on the landing page, route back home from sub-pages
  const HashLink = ({ hash, style, className, children }: { hash: string; style?: CSSProperties; className?: string; children: ReactNode }) =>
    routed
      ? <Link to={{ pathname: '/', hash }} onClick={shut} className={className} style={{ textDecoration: 'none', ...style }}>{children}</Link>
      : <a href={hash} onClick={(e) => go(e, hash)} className={className} style={{ textDecoration: 'none', ...style }}>{children}</a>;

  const DropItem = ({ label, desc }: { label: string; desc: string }) => (
    <>
      <b style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>{label}</b>
      <i style={{ display: 'block', fontStyle: 'normal', fontSize: 11.5, color: 'var(--fg-3)', marginTop: 1 }}>{desc}</i>
    </>
  );

  // a render function, NOT an inline component - an inline component's identity
  // changes every render, which would remount the button (and drop hover/focus)
  const navTrigger = (k: NavKey, label: string) => {
    const isOpen = open === k;
    return (
      <button
        key={k} ref={(el) => { trigRefs.current[k] = el; }}
        className="crw-navtop" style={pillLink} aria-expanded={isOpen} aria-haspopup="true"
        onMouseEnter={() => openMenu(k)} onMouseLeave={hoverClose}
        onClick={() => { if (isOpen) { glide.current = false; setOpen(null); } else openMenu(k); }}
      >
        {label}
        {/* the 300ms flip it visibly TURNS */}
        <CRWIcon d="chevdown" size={12} style={{ transition: 'transform 300ms var(--ease-out)', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
      </button>
    );
  };


  const links = (items: NavItem[]) => items.map((it) => (
    <HashLink key={it.label} hash={it.hash} className="crw-nav-dditem"><DropItem label={it.label} desc={it.desc} /></HashLink>
  ));

  const pillLink: CSSProperties = { fontSize: 13.5, padding: '7px 12px', borderRadius: 999 };

  // keep the last-open key so the panel fades out WITH its content on close
  if (open) lastShownRef.current = open;
  const shown = open ?? lastShownRef.current;
  const navContent = (k: NavKey) => links(k === 'get' ? NAV_GET : NAV_PROOF);

  // the shared viewport: ALWAYS mounted (its content is what gets measured -
  // gating the mount on a measurement would deadlock) but invisible until both
  // open and measured; glides (left/size) only between two open states
  const visible = Boolean(open && vp);
  const navViewport = (
    <div
      className="crw-nav-viewport" role="menu" aria-hidden={!visible}
      onMouseEnter={() => { if (closeTimer.current != null) window.clearTimeout(closeTimer.current); }}
      onMouseLeave={hoverClose}
      style={{
        left: vp?.left ?? 0, width: vp?.w, height: vp?.h, transformOrigin: 'top center',
        opacity: visible ? 1 : 0,
        transform: visible || reduced ? 'none' : 'translateY(-6px) scale(0.96)',
        pointerEvents: visible ? 'auto' : 'none',
        visibility: visible ? 'visible' : 'hidden',
        transition: reduced
          ? 'opacity 140ms var(--ease-out), visibility 140ms'
          : `opacity ${visible ? 200 : 140}ms var(--ease-out), transform 220ms var(--ease-out), visibility 220ms${glide.current ? ', left 260ms var(--ease-out), width 260ms var(--ease-out), height 260ms var(--ease-out)' : ''}`,
      }}
    >
      <div key={shown} ref={measureRef} className={`crw-nav-vpcontent${ghost && open && !reduced ? (ghost.dir === 'left' ? ' crw-vpin-r' : ' crw-vpin-l') : ''}`}>
        {navContent(shown)}
      </div>
      {ghost && open ? (
        <div key={`ghost-${ghost.n}`} aria-hidden="true" className={`crw-nav-vpcontent ${ghost.dir === 'left' ? 'crw-vpout-l' : 'crw-vpout-r'}`} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
          {navContent(ghost.k)}
        </div>
      ) : null}
    </div>
  );

  // The pill (N1) EXPANDED to content width (founder, 2026-07-17) with the
  // Features/Resources dropdowns back on board. Under 900px it relaxes to a
  // full-width bar so the burger sheet has room.
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, display: 'flex', justifyContent: 'center', padding: '16px 24px' }}>
      {/* an open dropdown or the mobile sheet forces the frost back on - a
          transparent pill can't visually anchor a floating panel */}
      <nav ref={navRef} className={`crw-glass crw-nav-frost crw-navpill${atop && !open && !menu ? ' crw-nav-atop' : ''}`} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2, padding: '7px 9px 7px 18px' }}>
        <HashLink hash="#top" className="crw-logo-link"><Logo size={22} /></HashLink>
        <span style={{ flex: 1 }}></span>
        <span className="crw-navlinks" style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {navTrigger('get', 'What you get')}
          {navTrigger('proof', 'Proof')}
          <HashLink hash="#deal" className="crw-navtop" style={pillLink}>The deal</HashLink>
        </span>
        {navViewport}
        <span className="crw-nav-join" style={{ marginLeft: 10, display: 'flex' }}>
          {/* Silver cut: the nav is chrome, no warm glow riding every screenful. */}
          <HashLink hash="#contact" style={{ display: 'flex' }}><MetalBtn size="sm" neutral>Get a quote</MetalBtn></HashLink>
        </span>
        <button className="crw-burger" aria-label={menu ? 'Close menu' : 'Open menu'} aria-expanded={menu} onClick={() => { setOpen(null); setMenu((m) => !m); }}>
          <CRWIcon d={menu ? 'x' : 'menu'} size={17} />
        </button>
      </nav>
      {/* mobile sheet - OUTSIDE the pill: the pill's backdrop-filter would trap
          position:fixed descendants in its own containing block */}
      {menu ? (
        <>
          <div className="crw-mm-scrim" onClick={shut} aria-hidden="true"></div>
          <div className="crw-glass crw-nav-frost crw-mobile-menu" ref={sheetRef} role="menu">
            <div className="crw-mm-scroll">
              <span className="crw-nav-grouplabel">What you get</span>
              <div className="crw-mm-grid">
                {NAV_GET.map((it) => <HashLink key={it.label} hash={it.hash} className="crw-mm-link">{it.label}</HashLink>)}
              </div>
              <span className="crw-nav-grouplabel">Proof</span>
              <div className="crw-mm-grid">
                {NAV_PROOF.map((it) => <HashLink key={it.label} hash={it.hash} className="crw-mm-link">{it.label}</HashLink>)}
              </div>
              <div className="crw-mm-grid" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <HashLink hash="#deal" className="crw-mm-link">The deal</HashLink>
              </div>
            </div>
            <div className="crw-mm-foot">
              <HashLink hash="#contact" style={{ display: 'flex' }}><MetalBtn style={{ flex: 1 }}>{CTA_LABEL}</MetalBtn></HashLink>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
